import { ENTITY_REGISTRY, ENTITY_SPAWN_CONFIG, ENTITY_COUNTS } from './config.js';

/**
 * Check if an entity type should be allowed to spawn based on configuration patterns
 * @param {string|object} entityType - The entity type name or object with name property
 * @param {string} category - The entity category (animals, plants, rocks, structures)
 * @returns {boolean} - Whether the entity should be allowed to spawn
 */
export function shouldSpawnEntityType(entityType, category = null) {
    const typeName = typeof entityType === 'string' ? entityType : entityType.name;

    // Verify the category is valid
    const validCategory = category && ENTITY_SPAWN_CONFIG[category.toLowerCase()];
    const categoryConfig = validCategory ? ENTITY_SPAWN_CONFIG[category.toLowerCase()] : null;

    // First check against global disabled patterns (these override everything)
    for (const pattern of ENTITY_SPAWN_CONFIG.global.disabledTypes) {
        if (new RegExp(pattern).test(typeName)) {
            return false;
        }
    }

    // Then check against category-specific disabled patterns if applicable
    if (categoryConfig && categoryConfig.disabledTypes) {
        for (const pattern of categoryConfig.disabledTypes) {
            if (new RegExp(pattern).test(typeName)) {
                return false;
            }
        }
    }

    // If no enabled patterns are specified in either global or category, allow all non-disabled types
    const hasGlobalEnabled = ENTITY_SPAWN_CONFIG.global.enabledTypes.length > 0;
    const hasCategoryEnabled = categoryConfig && categoryConfig.enabledTypes && categoryConfig.enabledTypes.length > 0;

    if (!hasGlobalEnabled && !hasCategoryEnabled) {
        return true;
    }

    // Check if matches any category-specific enabled pattern first
    if (hasCategoryEnabled) {
        for (const pattern of categoryConfig.enabledTypes) {
            if (new RegExp(pattern).test(typeName)) {
                return true;
            }
        }
    }

    // Then check against global enabled patterns
    if (hasGlobalEnabled) {
        for (const pattern of ENTITY_SPAWN_CONFIG.global.enabledTypes) {
            if (new RegExp(pattern).test(typeName)) {
                return true;
            }
        }
    }

    return false;
}

/**
 * Get the maximum allowed count for an entity type
 * @param {string|object} entityType - The entity type name or object with name property
 * @param {string} category - The entity category (animals, plants, rocks, structures)
 * @returns {number} - Maximum allowed count for this type
 */
export function getMaxEntityCount(entityType, category = null) {
    const typeName = typeof entityType === 'string' ? entityType : entityType.name;

    // If category is provided and valid, use its specific configuration
    const validCategory = category && ENTITY_SPAWN_CONFIG[category.toLowerCase()];
    const categoryConfig = validCategory ? ENTITY_SPAWN_CONFIG[category.toLowerCase()] : null;

    // First check for exact match in category-specific maxCounts
    if (categoryConfig && categoryConfig.maxCounts && categoryConfig.maxCounts[typeName]) {
        return categoryConfig.maxCounts[typeName];
    }

    // Then check for pattern matches in category-specific maxCounts
    if (categoryConfig && categoryConfig.maxCounts) {
        for (const [pattern, count] of Object.entries(categoryConfig.maxCounts)) {
            if (new RegExp(pattern).test(typeName)) {
                return count;
            }
        }
    }

    // Return category-specific default if available
    if (categoryConfig && categoryConfig.defaultMaxCount !== undefined) {
        return categoryConfig.defaultMaxCount;
    }

    // Finally, fall back to global default
    return ENTITY_SPAWN_CONFIG.global.defaultMaxCount;
}

/**
 * Check if an entity can be spawned based on current population count
 * @param {string|object} entityType - The entity type name or object with name property
 * @param {string} category - The entity category (animals, plants, rocks, structures)
 * @returns {boolean} - Whether another entity of this type can be spawned
 */
export function canSpawnMoreOfType(entityType, category) {
    if (!category || !ENTITY_COUNTS[category.toLowerCase()]) {
        console.warn(`Invalid category: ${category}`);
        return false;
    }

    const typeName = typeof entityType === 'string' ? entityType : entityType.name;
    const currentCount = getCurrentEntityCount(typeName, category);
    const maxCount = getMaxEntityCount(typeName, category);

    return currentCount < maxCount;
}

/**
 * Get the current count of a specific entity type
 * @param {string|object} entityType - The entity type name or object with name property
 * @param {string} category - The entity category (animals, plants, rocks, structures)
 * @returns {number} - Current count of this entity type
 */
export function getCurrentEntityCount(entityType, category) {
    if (!category || !ENTITY_COUNTS[category.toLowerCase()]) {
        return 0;
    }

    const typeName = typeof entityType === 'string' ? entityType : entityType.name;
    return ENTITY_COUNTS[category.toLowerCase()][typeName] || 0;
}

/**
 * Update the entity count for a specific type
 * @param {string|object} entityType - The entity type name or object with name property
 * @param {string} category - The entity category (animals, plants, rocks, structures)
 * @param {number} count - The new count value (or delta if increment is true)
 * @param {boolean} increment - Whether to increment the current count rather than set it
 */
export function updateEntityCount(entityType, category, count, increment = false) {
    if (!category || !ENTITY_COUNTS[category.toLowerCase()]) {
        console.warn(`Invalid category: ${category}`);
        return;
    }

    const typeName = typeof entityType === 'string' ? entityType : entityType.name;
    const categoryLower = category.toLowerCase();

    if (increment) {
        ENTITY_COUNTS[categoryLower][typeName] = (ENTITY_COUNTS[categoryLower][typeName] || 0) + count;
    } else {
        ENTITY_COUNTS[categoryLower][typeName] = count;
    }

    // Dispatch event for entity count change
    window.dispatchEvent(new CustomEvent('entityCountChanged', {
        detail: {
            category: categoryLower,
            entityType: typeName,
            count: ENTITY_COUNTS[categoryLower][typeName]
        }
    }));
}

/**
 * Calculate all entity counts from the world entities
 * @param {object} world - The game world object containing entities
 */
export function recalculateAllEntityCounts(world) {
    // Reset all counts
    for (const category of Object.keys(ENTITY_COUNTS)) {
        ENTITY_COUNTS[category] = {};
    }

    // Recalculate based on actual entities in the world
    for (const [category, info] of Object.entries(ENTITY_REGISTRY)) {
        if (!info.component || !world[info.component]) continue;

        // This assumes your ECS has a way to query entities with specific components
        // Adjust this based on your actual ECS implementation
        // Example: const entities = world.query(Has(world[info.component]));
        // Replace with your actual ECS query method
        console.warn("recalculateAllEntityCounts needs ECS query implementation");
        const entities = []; // Placeholder - replace with actual query

        for (const entity of entities) {
            const typeId = world[info.component].type[entity];
            if (typeId !== undefined) {
                // Convert type ID to type name if needed
                const typeName = getTypeNameFromId(typeId, category);
                if (typeName) {
                    ENTITY_COUNTS[category][typeName] = (ENTITY_COUNTS[category][typeName] || 0) + 1;
                }
            }
        }
    }

    // Dispatch event for entity counts recalculated
    window.dispatchEvent(new CustomEvent('entityCountsRecalculated'));
}

/**
 * Helper to convert type ID to type name
 * @param {number} typeId - The type ID from the component
 * @param {string} category - The entity category
 * @returns {string|null} - The type name or null if not found
 */
export function getTypeNameFromId(typeId, category) {
    // This needs to be implemented based on how types are defined/mapped
    console.warn(`getTypeNameFromId needs implementation for category: ${category}`);
    // Example:
    // if (category === 'animals' && ANIMAL_TYPES) { // Assuming ANIMAL_TYPES is imported/available
    //     return Object.keys(ANIMAL_TYPES).find(key => ANIMAL_TYPES[key].id === typeId);
    // }
    return null; // Replace with actual implementation
}

/**
 * Add a new entity type to the registry
 * @param {string} typeName - The name of the entity type
 * @param {string} category - The category to add it to
 */
export function registerEntityType(typeName, category) {
    if (!ENTITY_REGISTRY[category.toLowerCase()]) {
        console.warn(`Invalid category: ${category}`);
        return;
    }

    // Add to registry if not already there
    if (!ENTITY_REGISTRY[category.toLowerCase()].types.includes(typeName)) {
        ENTITY_REGISTRY[category.toLowerCase()].types.push(typeName);
        console.log(`Registered new entity type: ${typeName} in category ${category}`);
        // Consider dispatching an event if other systems need to know about new types
        // window.dispatchEvent(new CustomEvent('entityTypeRegistered', { detail: { typeName, category } }));
    }
}

/**
 * Update the spawn configuration for a specific entity type
 * @param {string} entityType - The entity type name
 * @param {string} category - The entity category
 * @param {object} config - Configuration object with properties to update
 * @param {boolean} [config.enabled] - Whether this type should be allowed to spawn
 * @param {number} [config.maxCount] - Maximum allowed count for this type
 */
export function updateEntitySpawnConfig(entityType, category, config = {}) {
    if (!category || !ENTITY_SPAWN_CONFIG[category.toLowerCase()]) {
        console.warn(`Invalid category: ${category}`);
        return;
    }

    const categoryLower = category.toLowerCase();
    const categoryConfig = ENTITY_SPAWN_CONFIG[categoryLower];

    // Ensure lists exist
    if (!categoryConfig.enabledTypes) categoryConfig.enabledTypes = [];
    if (!categoryConfig.disabledTypes) categoryConfig.disabledTypes = [];
    if (!categoryConfig.maxCounts) categoryConfig.maxCounts = {};

    // Enable/disable the entity type using exact match pattern
    if (config.enabled !== undefined) {
        const exactPattern = `^${entityType.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}$`; // Escape regex special chars

        // Remove from both lists first
        categoryConfig.enabledTypes = categoryConfig.enabledTypes.filter(pattern => pattern !== exactPattern);
        categoryConfig.disabledTypes = categoryConfig.disabledTypes.filter(pattern => pattern !== exactPattern);

        // Add to the appropriate list
        if (config.enabled) {
            categoryConfig.enabledTypes.push(exactPattern);
        } else {
            categoryConfig.disabledTypes.push(exactPattern);
        }
        console.log(`Set ${entityType} in ${category} enabled: ${config.enabled}`);
    }

    // Update max count
    if (config.maxCount !== undefined && config.maxCount >= 0) {
        categoryConfig.maxCounts[entityType] = config.maxCount;
        console.log(`Set ${entityType} in ${category} max count: ${config.maxCount}`);
    } else if (config.maxCount === null || config.maxCount === undefined) {
        // Allow removing a specific max count setting to fall back to defaults/patterns
        delete categoryConfig.maxCounts[entityType];
         console.log(`Removed specific max count for ${entityType} in ${category}`);
    }

    // Dispatch event for config change
    window.dispatchEvent(new CustomEvent('entitySpawnConfigChanged', {
        detail: {
            category: categoryLower,
            entityType,
            config
        }
    }));
}


/**
 * Enable or disable a specific entity type
 * @param {string} entityType - The entity type name
 * @param {string} category - The entity category
 * @param {boolean} enable - Whether to enable or disable the entity type
 */
export function setEntityTypeEnabled(entityType, category, enable) {
    updateEntitySpawnConfig(entityType, category, { enabled: enable });
}

/**
 * Set the maximum count for a specific entity type
 * @param {string} entityType - The entity type name
 * @param {string} category - The entity category
 * @param {number} maxCount - The maximum allowed count
 */
export function setEntityTypeMaxCount(entityType, category, maxCount) {
    updateEntitySpawnConfig(entityType, category, { maxCount });
}

/**
 * Enable or disable an entire category of entities
 * @param {string} category - The entity category
 * @param {boolean} enable - Whether to enable or disable the category
 */
export function setCategoryEnabled(category, enable) {
    if (!category || !ENTITY_SPAWN_CONFIG[category.toLowerCase()]) {
        console.warn(`Invalid category: ${category}`);
        return;
    }

    const categoryLower = category.toLowerCase();
    const categoryConfig = ENTITY_SPAWN_CONFIG[categoryLower];

    if (enable) {
        // Add a wildcard pattern to enable all entities in this category
        categoryConfig.enabledTypes = [".*"];
        // Clear the disabled types for this category
        categoryConfig.disabledTypes = [];
        console.log(`Enabled category: ${category}`);
    } else {
        // Add a wildcard pattern to disable all entities in this category
        categoryConfig.disabledTypes = [".*"];
        // Clear the enabled types for this category
        categoryConfig.enabledTypes = [];
        console.log(`Disabled category: ${category}`);
    }

    // Dispatch event for config change
    window.dispatchEvent(new CustomEvent('entityCategoryToggled', {
        detail: {
            category: categoryLower,
            enabled: enable
        }
    }));
} 